/**
 * useSalonEnrollments Hook
 *
 * Fetches all salon enrollments for the owner view (across courses).
 * Provides filtering helpers keyed by course slug.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getMySalonEnrollments } from '../../services/coursesService';
import type { SalonEnrollment, EnrollmentStatus } from '../../types/courses.types';

interface UseSalonEnrollmentsResult {
  isLoading: boolean;
  error: string | null;
  enrollments: SalonEnrollment[];
  getEnrollmentsForCourse: (courseSlug: string) => SalonEnrollment[];
  getActiveEnrollmentsForCourse: (courseSlug: string) => SalonEnrollment[];
  isEmployeeEnrolled: (courseSlug: string, employeeId: number) => boolean;
  getEmployeeEnrollmentStatus: (courseSlug: string, employeeId: number) => EnrollmentStatus | null;
  refresh: () => void;
}

export function useSalonEnrollments(
  employeeId: number | null,
  isOwner: boolean,
  isAuthenticated: boolean
): UseSalonEnrollmentsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrollments, setEnrollments] = useState<SalonEnrollment[]>([]);

  const fetchEnrollments = useCallback(async () => {
    if (!employeeId || !isOwner || !isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const { enrollments: list } = await getMySalonEnrollments();
      setEnrollments(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Der opstod en fejl');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, isOwner, isAuthenticated]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const enrollmentsByCourse = useMemo(() => {
    const map = new Map<string, SalonEnrollment[]>();
    for (const enrollment of enrollments) {
      const existing = map.get(enrollment.courseSlug) || [];
      existing.push(enrollment);
      map.set(enrollment.courseSlug, existing);
    }
    return map;
  }, [enrollments]);

  const getEnrollmentsForCourse = useCallback(
    (courseSlug: string): SalonEnrollment[] => enrollmentsByCourse.get(courseSlug) || [],
    [enrollmentsByCourse]
  );

  const getActiveEnrollmentsForCourse = useCallback(
    (courseSlug: string): SalonEnrollment[] =>
      (enrollmentsByCourse.get(courseSlug) || []).filter(
        (e) => e.status === 'pending' || e.status === 'approved'
      ),
    [enrollmentsByCourse]
  );

  const isEmployeeEnrolled = useCallback(
    (courseSlug: string, empId: number): boolean =>
      (enrollmentsByCourse.get(courseSlug) || []).some(
        (e) => e.employeeId === empId && (e.status === 'pending' || e.status === 'approved')
      ),
    [enrollmentsByCourse]
  );

  const getEmployeeEnrollmentStatus = useCallback(
    (courseSlug: string, empId: number): EnrollmentStatus | null => {
      const list = enrollmentsByCourse.get(courseSlug) || [];
      return list.find((e) => e.employeeId === empId)?.status ?? null;
    },
    [enrollmentsByCourse]
  );

  return {
    isLoading,
    error,
    enrollments,
    getEnrollmentsForCourse,
    getActiveEnrollmentsForCourse,
    isEmployeeEnrolled,
    getEmployeeEnrollmentStatus,
    refresh: fetchEnrollments,
  };
}
